import { useQuery } from '@tanstack/react-query';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { Collapsible, CollapsibleContent } from '@nesso/ui/primitives/collapsible';

import { isExpiredPaid, subscriptionMeOptions } from '@/entities/subscription';
import { AddClient } from '@/features/client/add-client';
import { AddClientContact } from '@/features/client/add-client-contact';
import { EditClientProfile } from '@/features/client/edit-client-profile';
import { QuickAddClient } from '@/features/client/quick-add-client';
import { SearchClientNavigation } from '@/features/client/search-client';
import { SaveServiceSheetHost } from '@/features/practice/save-service';
import { ScheduleSessionDialog } from '@/features/session/schedule-session';
import { Page } from '@/shared/ui/layout/page';
import { NavigationBar } from '@/shared/ui/navigation/navigation-bar';
import { ClientDetailsSheet, openClientDetailsSheet } from '@/widgets/client/client-details-sheet';
import { ClientBillingSheet } from '@/widgets/ledger/client-billing-sheet';
import { CreateMenu } from '@/widgets/navigation/create-menu';
import { ShareReferralLinkTrigger } from '@/widgets/referral/share-link-trigger';
import { TrialBanner } from '@/widgets/subscription/trial-banner';
import { UserDropdown } from '@/widgets/user/user-dropdown';

import { useCalendarPageState } from '../model/use-calendar-page-state';
import { useToday } from '../model/use-today';
import { CalendarPageCreateButton } from './calendar-page/calendar-page-create-button';
import { CalendarPageDateBar } from './calendar-page/calendar-page-date-bar';
import { CalendarPageDatePicker } from './calendar-page/calendar-page-date-picker';
import { CalendarPageDayHeader } from './calendar-page/calendar-page-day-header';
import { CalendarPageHeading } from './calendar-page/calendar-page-heading';
import { CalendarPageSidebar } from './calendar-page/calendar-page-sidebar';
import { CalendarPageTimeGrid } from './calendar-page/calendar-page-time-grid';

export function CalendarPage() {
    const today = useToday();
    const {
        selectedDay,
        shownMonth,
        isPickerOpen,
        selectDay,
        showNextMonth,
        showPreviousMonth,
        showMonth,
        goToToday,
        togglePicker,
    } = useCalendarPageState(today);
    const isMobile = useBreakpointDown('sm');
    // Always true here: `calendarBeforeLoad` throws `notFound()` for any account without
    // ['CLINICIAN', 'EHR_BASE'], so this page never renders without EHR access.
    const hasEhrAccess = true;
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const isExpired = isExpiredPaid(subscription);

    return (
        <Page>
            <NavigationBar
                leading={<CalendarPageHeading />}
                actions={
                    <>
                        {!isExpired && (
                            <>
                                <SearchClientNavigation
                                    hasEhrAccess={hasEhrAccess}
                                    onSelectClient={openClientDetailsSheet}
                                />
                                <CreateMenu hasEhrAccess={hasEhrAccess} />
                            </>
                        )}
                        <ShareReferralLinkTrigger />
                    </>
                }
                dropdown={<UserDropdown />}
                banner={<TrialBanner />}
                mobileBanner={<TrialBanner variant="mobile" />}
            />
            <div className="flex flex-1 min-h-0">
                {isMobile ? (
                    // `overflow-y-auto`, not `overflow-clip`: on a short screen the header, date
                    // bar and open picker can outgrow the viewport on their own, and the column
                    // scrolling (instead of squeezing `<main>` to nothing) keeps the day view
                    // reachable. `CalendarPageDateBar` stays `sticky` so it survives that scroll.
                    <div className="flex flex-1 flex-col min-h-0 overflow-y-auto rounded-t-sm bg-card">
                        <CalendarPageDateBar
                            selectedDay={selectedDay}
                            shownMonth={shownMonth}
                            today={today}
                            isPickerOpen={isPickerOpen}
                            onToggle={togglePicker}
                            onToday={goToToday}
                        />
                        {/* Two Collapsibles, not a ternary, so the day row fades out while the month slides down from the date bar. */}
                        <Collapsible open={isPickerOpen}>
                            <CollapsibleContent className="group shrink-0 data-[state=open]:motion-safe:animate-collapsible-reveal data-[state=closed]:motion-safe:animate-collapsible-conceal">
                                <div className="group-data-[state=open]:motion-safe:animate-collapsible-slide-in group-data-[state=closed]:motion-safe:animate-collapsible-slide-out">
                                    <CalendarPageDatePicker
                                        selectedDay={selectedDay}
                                        shownMonth={shownMonth}
                                        today={today}
                                        selectDay={selectDay}
                                        showNextMonth={showNextMonth}
                                        showPreviousMonth={showPreviousMonth}
                                        showMonth={showMonth}
                                    />
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                        <Collapsible open={!isPickerOpen}>
                            <CollapsibleContent className="group shrink-0 data-[state=open]:motion-safe:animate-collapsible-reveal data-[state=closed]:motion-safe:animate-collapsible-conceal">
                                <div className="group-data-[state=open]:motion-safe:animate-in group-data-[state=open]:fade-in group-data-[state=closed]:motion-safe:animate-out group-data-[state=closed]:fade-out group-data-[state=closed]:fill-mode-forwards duration-200 ease-out">
                                    <CalendarPageDayHeader day={selectedDay} />
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                        {/* `min-h-48`, not `min-h-0`: the day view keeps a few hours visible even
                            while the column above has grown past the viewport. */}
                        <main className="flex flex-1 min-h-48 flex-col">
                            <CalendarPageTimeGrid selectedDay={selectedDay} view="day" />
                        </main>
                    </div>
                ) : (
                    <>
                        <aside className="w-64 shrink-0 overflow-y-auto">
                            <CalendarPageSidebar
                                selectedDay={selectedDay}
                                shownMonth={shownMonth}
                                today={today}
                                selectDay={selectDay}
                                showMonth={showMonth}
                                isExpired={isExpired}
                            />
                        </aside>
                        <main className="flex flex-1 min-h-0 flex-col mr-2 mb-2 overflow-clip rounded-lg bg-card">
                            <CalendarPageTimeGrid selectedDay={selectedDay} view="week" />
                        </main>
                    </>
                )}
            </div>
            {isMobile && !isExpired && <CalendarPageCreateButton />}
            <QuickAddClient />
            <ScheduleSessionDialog />
            <SaveServiceSheetHost />
            <AddClient />
            <ClientDetailsSheet />
            <ClientBillingSheet />
            <EditClientProfile />
            <AddClientContact />
        </Page>
    );
}
